import { Icon } from "../msd/Icon";
import { Avatar, CARD, DISP, Field, INPUT, Page, PageHead, cx } from "../msd/ui";

// Pieces shared by the Book a service screens (the two step-by-step wizards and
// the QR pre-filled form), rebuilt from design/maruti-service-desk3.html.

export const inputCls = INPUT;

// .fl-h
export const HINT = "text-[12.5px] text-msd-muted";

const Required = () => <span className="text-[#F04438]"> *</span>;

// .fl-l
export function FieldLabel({ children, required }) {
  return (
    <span className="block text-[13.5px] font-semibold text-msd-ink2">
      {children}
      {required ? <Required /> : null}
    </span>
  );
}

// .fl — one labelled control. `group` is for a field made of several controls
// (a picker, a pair of inputs), where a wrapping <label> would steal their clicks.
export function FormField({ label, required = false, optional = false, hint, group = false, className, children }) {
  if (!group) {
    return (
      <Field
        label={
          <>
            {label}
            {required ? <Required /> : null}
          </>
        }
        optional={optional}
        hint={hint}
        className={className}
      >
        {children}
      </Field>
    );
  }
  return (
    <div className={cx("flex flex-col gap-[7px]", className)}>
      <FieldLabel required={required}>{label}</FieldLabel>
      {children}
      {hint ? <span className={HINT}>{hint}</span> : null}
    </div>
  );
}

// .in-pre — the +91 box. Props go to the <input>.
export function PhoneInput(props) {
  return (
    <div className="flex items-center rounded-xl bg-msd-ground focus-within:shadow-[0_0_0_2px_#3A5F94]">
      <span className="select-none pl-[14px] pr-1 text-[16px] text-msd-muted">+91</span>
      <input
        className="h-[46px] w-full min-w-0 border-0 bg-transparent pl-[6px] pr-[14px] text-[16px] text-msd-ink outline-none placeholder:text-msd-faint"
        {...props}
      />
    </div>
  );
}

// .chips2 button
export const chipCls = (on) =>
  cx(
    "h-[38px] rounded-full border-0 px-4 text-[14.5px] font-medium",
    on
      ? "bg-[#1C1C1E] text-white [:root[data-theme=dark]_&]:bg-white [:root[data-theme=dark]_&]:text-black"
      : "bg-msd-ground text-msd-ink",
  );

// A line of guidance at the top of a step
export const NOTE = "m-0 text-[14px] text-msd-muted";

// .plate-in, without its uppercase: the group is named exactly as typed.
export const PLATE = "!h-14 !text-[20px] font-bold tracking-[.06em]";

// .row3 — two fields side by side, stacked on a phone
export const ROW2 = "grid grid-cols-2 gap-3 max-[640px]:grid-cols-1";

// .bk-steps — the row of step pills. `steps` is [{ key, label, subtitle }]. The
// pills only show progress: moving between steps stays with Back and Continue,
// which is where each step's rules are checked.
export function StepSidebar({ steps, currentStep, completedUpTo }) {
  return (
    <ol className="m-0 flex list-none flex-wrap gap-[6px] p-0">
      {steps.map((s, i) => {
        const active = i === currentStep;
        const done = !active && i < completedUpTo;
        return (
          <li
            key={s.key}
            title={s.subtitle}
            aria-current={active ? "step" : undefined}
            className={cx(
              "flex h-[38px] items-center gap-2 rounded-full pl-[6px] pr-[14px] text-[14px] font-semibold",
              active ? "bg-msd-btn text-white" : done ? "bg-msd-ground text-msd-ink" : "bg-msd-ground text-msd-muted",
            )}
          >
            <span
              className={cx(
                "flex h-[26px] w-[26px] items-center justify-center rounded-full text-[13px]",
                active ? "bg-white text-msd-ink" : done ? "bg-[#17B26A] text-white" : "bg-msd-surface",
              )}
            >
              {done ? <Icon name="check" size={12} stroke={3} /> : i + 1}
            </span>
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}

// .an-card.bk-form
export const FORM_CARD = cx(CARD, "flex min-w-0 flex-col gap-[18px] px-[26px] py-6");
// .bk-body
export const STEP_BODY = "flex min-h-[300px] flex-col gap-[18px]";

// .bk-foot — Back on the left, the step's main button on the right
export function WizardFoot({ children }) {
  return <div className="flex items-center justify-between gap-3 pt-[14px] shadow-[0_-1px_0_var(--line)]">{children}</div>;
}

// The page: heading, then the form card beside the sticky group preview.
export function BookingLayout({ sub, preview, children }) {
  return (
    <Page data-testid="create-group-page">
      <PageHead title="Book a service" sub={sub} />
      <div className="grid grid-cols-[minmax(0,1fr)_380px] items-start gap-[18px] max-[1100px]:grid-cols-1">
        {children}
        {preview}
      </div>
    </Page>
  );
}

// The last step's check list. `rows` is [[label, value], ...].
export function ReviewList({ rows }) {
  return (
    <dl className="m-0 text-[14.5px]">
      {rows.map(([k, v], i) => (
        <div key={k} className={cx("flex gap-3 py-[11px]", i > 0 && "border-t border-msd-line")}>
          <dt className="w-28 shrink-0 text-msd-muted">{k}</dt>
          <dd className="m-0 min-w-0 break-words font-medium text-msd-ink">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---- Group preview ----------------------------------------------------------------

// The chosen model's catalog photo and the line under the group name.
export function vehiclePreview(vehicle, catalogRows) {
  const model = vehicle.notListed ? null : (catalogRows || []).find((r) => r.id === Number(vehicle.catalogId)) || null;
  const name = vehicle.notListed ? vehicle.typed.trim() : model?.name;
  return {
    image: model?.thumb_url || model?.hero_url || "",
    sub: [name, vehicle.variant.trim(), vehicle.fuel.trim(), vehicle.year].filter(Boolean).join(" · "),
  };
}

// "12 Oct 18:00"
export function deliveryLabel(date, time) {
  if (!date) return "Not set";
  const at = new Date(`${date}T00:00`);
  if (Number.isNaN(at.getTime())) return "Not set";
  return `${at.toLocaleDateString("en", { day: "numeric", month: "short" })}${time ? ` ${time}` : ""}`;
}

// The two people the form itself names. The rest of the workshop's team is
// invited by the server, which is the only place that list exists.
export function bookingMembers({ customerName, customerPhone, advisorName, advisorRole, hasWorkshop, advisorPhone }) {
  return [
    { key: "customer", name: customerName || "Customer", role: "Customer", detail: customerPhone || "Number not added", empty: !customerName, customer: true },
    { key: "advisor", name: advisorName || (hasWorkshop ? "Choose an advisor" : advisorRole), role: advisorRole, detail: advisorPhone || "Number not added", empty: !advisorName },
  ];
}

// .bk-prev — what the WhatsApp group will look like, from what is filled in so far.
export function GroupPreview({ image, name, sub, description, meta, members }) {
  return (
    <aside className={cx(CARD, "sticky top-0 min-w-0 px-[26px] py-6 max-[1100px]:static")} data-testid="booking-preview">
      <div className="text-[13px] font-semibold tracking-[.02em] text-msd-muted">GROUP PREVIEW</div>
      <div className="mb-[6px] mt-[14px] text-center">
        {image ? (
          <img src={image} alt="" className="mx-auto block h-28 w-28 rounded-full bg-[#EEF0F4] object-cover shadow-msd-e2" />
        ) : (
          <span className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-[#EEF0F4] text-[#8E8E93] shadow-msd-e2">
            <Icon name="car" size={44} stroke={1.5} />
          </span>
        )}
        <div className={cx("mt-[10px] break-words text-[22px] font-bold text-msd-ink", DISP)}>{name || "Vehicle number"}</div>
        <div className="text-[13px] text-msd-muted">{sub || "Model"}</div>
        {description ? <div className="mt-[6px] break-words text-[14px] text-msd-ink">{description}</div> : null}
      </div>
      <div className="mt-[10px] grid grid-cols-3 gap-2 rounded-[14px] bg-msd-soft p-3">
        {meta.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <span className="block text-[11.5px] text-msd-muted">{k}</span>
            <b className="block truncate text-[13.5px] font-semibold text-msd-ink" title={v}>
              {v}
            </b>
          </div>
        ))}
      </div>
      <div className="mb-1 mt-[14px] flex justify-between gap-3 text-[13px] text-msd-muted">
        <span className="font-semibold">Members</span>
        <span className="text-right">The workshop team joins when the group is created</span>
      </div>
      {members.map((m, i) => (
        <div key={m.key} className={cx("flex items-center gap-3 py-2 text-[14.5px] text-msd-ink", i > 0 && "border-t border-msd-line", m.empty && "opacity-55")}>
          {m.empty ? (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-msd-faint shadow-[inset_0_0_0_1.5px_var(--line2)]">
              <Icon name="people" size={14} stroke={2} />
            </span>
          ) : (
            <Avatar name={m.name} size={32} className={m.customer ? "!bg-msd-btn !text-white" : undefined} />
          )}
          <span className="min-w-0 grow">
            <b className="block truncate font-medium">{m.name}</b>
            {m.detail ? <span className="block text-[13px] text-msd-muted">{m.detail}</span> : null}
          </span>
          <span className="shrink-0 text-[13px] text-msd-muted">{m.role}</span>
        </div>
      ))}
    </aside>
  );
}
